/**
 * One of the six stroke motifs a course signature draws with (ADR 0035).
 *
 * The same 24-unit glyph draws the 20 px mark in registers and rail rows, the
 * 40 px mark in a glance tile, and the legacy signature tile; the cover art is
 * the same motif cropped large. Stroke 2.5, round caps; the lattice is four
 * filled circles. Decorative by contract: it takes its colour from the parent.
 */
import type { ReactNode, SVGProps } from "react";

import { COURSE_MOTIFS, type CourseMotif } from "@/lib/course-signature";
import { cn } from "@/lib/utils";

const GLYPHS: Record<CourseMotif, ReactNode> = {
  arc: <path d="M5 19a14 14 0 0 1 14-14" />,
  diagonals: <path d="M5 15 15 5M9 19 19 9" />,
  lattice: (
    <g fill="currentColor" stroke="none">
      <circle cx="8" cy="8" r="2.5" />
      <circle cx="16" cy="8" r="2.5" />
      <circle cx="8" cy="16" r="2.5" />
      <circle cx="16" cy="16" r="2.5" />
    </g>
  ),
  bars: <path d="M7 8v8M12 5v14M17 8v8" />,
  ring: <circle cx="12" cy="12" r="7" />,
  diamond: <path d="M12 4l8 8-8 8-8-8z" />,
};

export function CourseGlyph({
  motif,
  className,
  ...props
}: Omit<SVGProps<SVGSVGElement>, "children"> & {
  /** The signature's 0-based motif index. */
  motif: number;
}) {
  const name = COURSE_MOTIFS[motif] ?? COURSE_MOTIFS[0];
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      data-course-motif={name}
      className={cn("shrink-0", className)}
      {...props}
    >
      {GLYPHS[name]}
    </svg>
  );
}
